/**
 * scoutLog — Standalone diagnostic event logger for scout_activity_log.
 *
 * Fire-and-forget: never throws, never blocks UI.
 * Used by useActivityTracker (session diagnostics) and useMorningSync (sync lifecycle).
 *
 * Events emitted:
 * - session_diagnostics: build_hash, sw_status, last_sync_at, cache_age_ms, idb_key_count
 * - sync_start: triggered when morning sync begins
 * - sync_complete: mission_count, lead_count, duration_ms
 * - sync_error: error message and context
 * - sync_skipped: reason (interval_not_reached, offline, no_email, already_syncing)
 * - sw_update_found: new SW version detected
 * - sw_activated: new SW took control
 * - pull_to_refresh: manual refresh triggered
 * - cache_cleared: on logout/session expiry
 */

import { getBuildIdentity } from './buildIdentity'

// ── Supabase session reader (mirrors useActivityTracker) ────────────────
//
// Reads the persisted Supabase session written by `@appic/service-layer`
// (storageKey 'sb-clanda-sl' — see packages/service-layer/src/supabaseClient.ts).
// Returns null when:
//   - no auth-token key is set (logged out, or setSession never ran)
//   - the JSON is malformed
//   - either field is missing on the parsed session
//
// Prior versions read `sb-${ref}-auth-token` (the supabase-js DEFAULT key)
// which our clients NEVER write to — they all set explicit storageKeys.
// That bug silently dropped every scout_activity_log write on every
// platform (BUG-SL-SCOUT-REP-CANNOT-READ-OWN-MISSIONS-2026-05-26).

const SL_AUTH_STORAGE_KEY = 'sb-clanda-sl'

/**
 * Read the persisted SL Supabase session. Exported so the unload-flush beacon
 * (offline/unloadFlush.ts) authenticates its keepalive POST through the SAME reader
 * as every other scout_activity_log write — one home for "who is the rep", so a
 * storage-key drift can never make one path silently unauthenticated (the exact
 * BUG-SL-SCOUT-REP-CANNOT-READ-OWN-MISSIONS shape).
 */
export function getSupabaseSession(): { accessToken: string; userId: string } | null {
  try {
    const raw = localStorage.getItem(SL_AUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const accessToken = parsed?.access_token as string | undefined
    const userId = parsed?.user?.id as string | undefined
    if (!accessToken || !userId) return null
    return { accessToken, userId }
  } catch {
    return null
  }
}

// ── Session ID (set by useActivityTracker on mount) ─────────────────────

let _sessionId = 'pre-init'

export function setScoutLogSessionId(id: string): void {
  _sessionId = id
}

// ── Build hash ──────────────────────────────────────────────────────────

/**
 * The commit this bundle was built from, or 'unresolved'.
 *
 * It used to answer 'dev' — in PRODUCTION, on every build, because the prod
 * image has no git binary (see buildIdentity.ts). The `try/catch → 'unknown'`
 * that wrapped it was dead code twice over: the `typeof` guard already made a
 * ReferenceError impossible, and the catch existed only to soften a failure that
 * had already been softened into a lie. One rule, one home.
 */
export function getBuildHash(): string {
  return getBuildIdentity().commit
}

// ── Service Worker status ───────────────────────────────────────────────

export async function getSWStatus(): Promise<{
  state: string
  scriptURL: string | null
  waiting: boolean
}> {
  try {
    if (!('serviceWorker' in navigator)) return { state: 'unsupported', scriptURL: null, waiting: false }
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return { state: 'no-registration', scriptURL: null, waiting: false }
    const active = reg.active
    return {
      state: active?.state ?? (reg.installing ? 'installing' : reg.waiting ? 'waiting' : 'unknown'),
      scriptURL: active?.scriptURL ?? null,
      waiting: !!reg.waiting,
    }
  } catch {
    return { state: 'error', scriptURL: null, waiting: false }
  }
}

// ── Core log function ───────────────────────────────────────────────────

export function logScoutEvent(
  event: string,
  payload: Record<string, unknown> = {},
  route?: string,
): void {
  try {
    const session = getSupabaseSession()
    if (!session) return

    const supabaseUrl = ((import.meta.env.VITE_SUPABASE_URL || '') as string).replace(/\/$/, '')
    const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '') as string
    if (!supabaseUrl || !anonKey) return

    const entry = {
      user_id: session.userId,
      session_id: _sessionId,
      event,
      payload,
      route: route ?? (typeof window !== 'undefined' ? window.location.pathname : null),
      created_at: new Date().toISOString(),
    }

    fetch(`${supabaseUrl}/rest/v1/scout_activity_log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${session.accessToken}`,
        'Prefer': 'return=minimal',
      },
      body: JSON.stringify([entry]),
    }).catch(() => {
      // Telemetry is fire-and-forget — never break the app
    })
  } catch {
    // Never throw from diagnostic logging
  }
}

/**
 * Same insert pattern as logScoutEvent but awaited + throws on failure.
 *
 * WHY: the debug-report submit needs to know if the write failed (so the
 * UI can offer the download fallback). The supabase-js client with
 * persistSession: false can't insert under `auth.uid() = user_id` RLS —
 * only the raw-fetch + Bearer-token pattern works. One shared implementation.
 *
 * `opts.timeoutMs` (FR-DEBUG-REPORT-SUBMIT-HANGS-LARGE-PAYLOAD, Steffen
 * 2026-04-25 19:40): debug-report submits can be 1-5 MB (screenshot +
 * console buffer); without a client-side bound the fetch hangs indefinitely
 * on bad mobile connectivity. Callers pass a timeout; on timeout the
 * underlying fetch is aborted and an error with `status === 'timeout'` is
 * thrown, letting the caller invoke its fallback (downloadBundle).
 */
export async function postActivityLog(
  event: string,
  payload: Record<string, unknown>,
  opts: { route?: string | null; timeoutMs?: number } = {},
): Promise<void> {
  const session = getSupabaseSession()
  if (!session) throw new Error('No authenticated Supabase session — cannot post to scout_activity_log')

  const supabaseUrl = ((import.meta.env.VITE_SUPABASE_URL || '') as string).replace(/\/$/, '')
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '') as string
  if (!supabaseUrl || !anonKey) throw new Error('Supabase config missing (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)')

  const entry = {
    user_id: session.userId,
    session_id: _sessionId,
    event,
    payload,
    route: opts.route ?? (typeof window !== 'undefined' ? window.location.pathname : null),
    created_at: new Date().toISOString(),
  }

  // Build an AbortController for timeout. AbortSignal.timeout is widely
  // supported (Chrome ≥ 103, Firefox ≥ 100, Safari ≥ 16) but we fall back
  // to a manual controller for older runtimes / SSR / jsdom so the code
  // path stays testable without special stubbing.
  let signal: AbortSignal | undefined
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined
  if (opts.timeoutMs && opts.timeoutMs > 0) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      signal = AbortSignal.timeout(opts.timeoutMs)
    } else if (typeof AbortController !== 'undefined') {
      const controller = new AbortController()
      signal = controller.signal
      timeoutHandle = setTimeout(() => controller.abort(), opts.timeoutMs)
    }
  }

  try {
    const res = await fetch(`${supabaseUrl}/rest/v1/scout_activity_log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${session.accessToken}`,
        'Prefer': 'return=minimal',
      },
      body: JSON.stringify([entry]),
      signal,
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      const err = new Error(`HTTP ${res.status}: ${text.slice(0, 500) || res.statusText}`) as Error & { status?: number; raw?: string }
      err.status = res.status
      err.raw = text
      throw err
    }
  } catch (err) {
    // AbortError (timeout) → surface as a typed error the caller can detect
    // without inspecting DOMException internals. `status` is set to the
    // sentinel 'timeout' string so callers can branch without string-matching
    // `err.name === 'AbortError'`.
    const e = err as { name?: string } & Error
    if (e?.name === 'AbortError') {
      const wrapped = new Error(`scout_activity_log submit timed out after ${opts.timeoutMs}ms`) as Error & { status?: string; cause?: unknown }
      wrapped.status = 'timeout'
      wrapped.cause = err
      throw wrapped
    }
    throw err
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle)
  }
}

export interface RecentActivityEntry {
  event: string
  payload: unknown
  created_at: string
}

/**
 * Discriminated result for {@link getRecentActivity}. NEVER-DARK: a read that
 * fails (no session, missing config, non-2xx, network throw, unparseable body)
 * must be DISTINGUISHABLE from a genuinely empty history. The prior signature
 * returned `[]` on every one of those, so the debug-report bundler could not
 * tell "this rep has no recent activity" from "the read silently failed" — a
 * dark surface (D-33 R1).
 */
export type RecentActivityResult =
  | { ok: true; activities: RecentActivityEntry[] }
  | { ok: false; error: string }

/**
 * Reads the caller's own recent scout_activity_log entries using the same
 * Bearer-token pattern as postActivityLog. The supabase-js client with
 * persistSession: false can't pass the `auth.uid() = user_id` RLS on reads
 * either — so the anon-authed .from().select() path silently returns an
 * empty array. Used by the debug-report bundler to populate recentActivity.
 *
 * Returns a typed {@link RecentActivityResult}: `{ok:true, activities}` on a
 * confirmed read, `{ok:false, error}` on ANY failure — the caller decides how
 * to surface it instead of the failure vanishing into an empty list.
 */
export async function getRecentActivity(
  userId: string,
  limit = 30,
): Promise<RecentActivityResult> {
  const session = getSupabaseSession()
  if (!session) return { ok: false, error: 'No authenticated Supabase session — cannot read scout_activity_log' }
  const supabaseUrl = ((import.meta.env.VITE_SUPABASE_URL || '') as string).replace(/\/$/, '')
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '') as string
  if (!supabaseUrl || !anonKey) {
    return { ok: false, error: 'Supabase config missing (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)' }
  }
  const params = new URLSearchParams()
  params.set('user_id', `eq.${userId}`)
  params.set('select', 'event,payload,created_at')
  params.set('order', 'created_at.desc')
  params.set('limit', String(limit))
  try {
    const res = await fetch(`${supabaseUrl}/rest/v1/scout_activity_log?${params.toString()}`, {
      headers: {
        'apikey': anonKey,
        'Authorization': `Bearer ${session.accessToken}`,
      },
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      return { ok: false, error: `HTTP ${res.status}: ${text.slice(0, 500) || res.statusText}` }
    }
    const body = (await res.json()) as unknown
    return { ok: true, activities: Array.isArray(body) ? (body as RecentActivityEntry[]) : [] }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) }
  }
}
